import { money2 } from "../lux";

const Row = ({ label, value, hint, indent, strong, tone, testid }) => (
  <div
    className={`flex items-baseline justify-between gap-4 py-2 ${strong ? "border-t border-border mt-1 pt-2.5" : "border-b border-border/40"}`}
    data-testid={testid}
  >
    <span className={`${indent ? "pl-5 text-muted-foreground" : ""} ${strong ? "font-heading text-base" : "text-sm"}`}>
      {label}
      {hint && <span className="ml-2 text-[11px] text-muted-foreground/70">{hint}</span>}
    </span>
    <span className={`tnum whitespace-nowrap ${strong ? "font-heading text-lg" : "text-sm"} ${tone === "gold" ? "text-primary" : ""} ${tone === "red" ? "text-red-400" : ""} ${tone === "green" ? "text-emerald-400" : ""}`}>
      {value}
    </span>
  </div>
);

const Group = ({ children, title }) => (
  <div className="mb-7">
    <div className="label-mono text-primary/70 mb-1.5">{title}</div>
    {children}
  </div>
);

// The operating statement. Revenue is the full amount charged to the client.
export function PnlStatement({ d, basis }) {
  const rev = d?.revenue || {};
  const c = d?.direct_costs || {};
  const rec = d?.recoveries || {};
  const cashMode = basis === "cash";
  return (
    <div className="border border-border bg-card p-6 sm:p-8 max-w-2xl" data-testid="pnl-statement">
      <Group title={cashMode ? "Cash received" : "Revenue"}>
        <Row indent label="Rental revenue" value={money2(rev.rental)} testid="pnl-rental" />
        <Row indent label="Chauffeur revenue" value={money2(rev.chauffeur)} testid="pnl-chauffeur" />
        <Row indent label="Tour revenue" value={money2(rev.tour)} testid="pnl-tour" />
        {!cashMode && <Row indent label="Delivery & other charges" value={money2(rev.delivery_other)} testid="pnl-delivery" />}
        {cashMode && <Row indent label="Invoice payments" value={money2(rev.invoice_payments)} testid="pnl-invoice-payments" />}
        <Row strong tone="gold" label={cashMode ? "Total cash collected" : "Gross revenue"} value={money2(rev.gross)} testid="pnl-gross-revenue" />
      </Group>

      {cashMode ? (
        <p className="text-xs text-muted-foreground leading-relaxed" data-testid="pnl-cash-note">
          Cash mode shows money actually received in this range — deposits recorded on bookings plus
          payments recorded against invoices. Costs, owner cuts and payouts have no payment date of
          their own, so profit is only stated on the accrual basis. Switch to <b>Accrual</b> for the
          full statement.
        </p>
      ) : (
        <>
          <Group title="Direct costs">
            <Row indent label="Owner cuts" hint="3rd-party splits" value={money2(c.owner_cuts)} testid="pnl-owner-cuts" />
            <Row indent label="Affiliate payouts" value={money2(c.affiliate_payouts)} testid="pnl-affiliate" />
            <Row indent label="Driver costs" value={money2(c.driver)} testid="pnl-driver" />
            <Row indent label="Fuel costs" value={money2(c.fuel)} testid="pnl-fuel" />
            <Row indent label="Other trip costs" value={money2(c.other_trip)} testid="pnl-other-trip" />
            <Row strong tone="red" label="Total direct costs" value={money2(c.total)} testid="pnl-total-costs" />
          </Group>

          <Group title="Profit">
            <Row strong tone="green" label="Gross profit" value={money2(d?.gross_profit)} testid="pnl-gross-profit" />
            <Row indent label="Gross margin" value={`${d?.gross_margin ?? 0}%`} testid="pnl-gross-margin" />
          </Group>

          <Group title="Recoveries">
            <Row indent label="Tickets & tolls recovered" value={money2(rec.tickets_tolls)} testid="pnl-tickets" />
            <Row indent label="Damage / other recoveries" value={money2(rec.damage_other)} testid="pnl-damage" />
            <Row strong tone="gold" label="Net operating profit" value={money2(rec.net_operating_profit)} testid="pnl-net-operating" />
          </Group>
        </>
      )}
    </div>
  );
}
